import { apiClient, ApiResponse } from "./client";

// ============================================================================
// AIQ Types (matching backend DTOs)
// ============================================================================

export interface AiqComponentStatus {
  status: 'not_started' | 'in_progress' | 'completed';
  attemptCount: number;
  bestScore: number | null;
  latestScore: number | null;
  passed: boolean;
  completedAt: string | null;
}

export interface AiqStatusResponse {
  preAiq: AiqComponentStatus;
  postAiq: AiqComponentStatus;
  learningProgressPercent: number;
  isLearningComplete: boolean;
  canStartLearning: boolean;
  canStartPostAiq: boolean;
  /** Pre-AIQ가 Configuration에서 활성화되어 있는지 여부 */
  preAiqEnabled: boolean;
  /** Post-AIQ가 Configuration에서 활성화되어 있는지 여부 */
  postAiqEnabled: boolean;
}

// Frontend convenience types (derived from backend response)
export interface AIQTestStatus {
  status: 'not_started' | 'in_progress' | 'completed' | 'locked' | 'available';
  score: number | null;
  correctAnswers: number | null;
  totalQuestions: number;
  completedAt: string | null;
  unlockCondition?: string;
}

export interface AIQReportStatus {
  status: 'scheduled' | 'available';
  reportEnabled: boolean;
  availableDate: string | null;
  message: string;
}

export interface AIQStatus {
  productId: number;
  classProductId: number;
  preTest: AIQTestStatus;
  postTest: AIQTestStatus;
  report: AIQReportStatus;
  /** Pre-AIQ가 Configuration에서 활성화되어 있는지 여부 */
  preAiqEnabled: boolean;
  /** Post-AIQ가 Configuration에서 활성화되어 있는지 여부 */
  postAiqEnabled: boolean;
}

// ============================================================================
// Pool Analysis Types
// ============================================================================

export interface AiqPoolAnalysisResponse {
  productId: number;
  totalQuizCount: number;
  uniqueQuizCount: number;
  byDifficulty: Record<number, number>;
  selectionRule: {
    perCurriculum: number;
    byLevel: Record<number, number>;
    totalTarget: number;
  };
  byCurriculum: AiqCurriculumPoolInfo[];
  issues: string[];
}

export interface AiqCurriculumPoolInfo {
  curriculumId: number;
  curriculumName: string | null;
  totalCount: number;
  byDifficulty: Record<number, number>;
  meetsRequirement: boolean;
  shortfalls: Record<number, number>;
  byLesson: AiqLessonPoolInfo[];
}

export interface AiqLessonPoolInfo {
  lessonId: number;
  lessonName: string | null;
  totalCount: number;
  byDifficulty: Record<number, number>;
}

export interface AiqSimulateSelectionResponse {
  totalSelected: number;
  byCurriculum: {
    curriculumId: number;
    curriculumName: string | null;
    selectedCount: number;
    byDifficulty: Record<number, number>;
  }[];
  selectedQuizzes: AiqSelectedQuizInfo[];
  warnings: string[];
}

export interface AiqSelectedQuizInfo {
  quizId: string;
  difficultyLevel: number;
  curriculumId: number;
  lessonId: number;
  lessonName: string | null;
  quizType: string | null;
}

// ============================================================================
// Domain Analysis Types
// ============================================================================

export interface AiqDomainAnalysisResponse {
  productId: number;
  totalAiqQuizzes: number;
  domainStats: {
    abstractionCount: number;
    changeCount: number;
    relationCount: number;
    noDomainCount: number;
    multiDomainCount: number;
    combinations: Record<string, number>;
  };
  quizzes: AiqQuizDomainInfo[];
}

export interface AiqQuizDomainInfo {
  quizId: string;
  title: string | null;
  difficultyLevel: number;
  isAbstraction: boolean;
  isChange: boolean;
  isRelation: boolean;
  domains: string[];
  lessonName: string | null;
  curriculumName: string | null;
}

// ============================================================================
// Report Types
// ============================================================================

export interface AiqIndividualReportResponse {
  summary: AiqReportSummary;
  questions: AiqQuestionComparison[];
  byDomain: AiqDomainAnalysis[];
  byDifficulty: AiqDifficultyAnalysis[];
  timeAnalysis: AiqTimeAnalysis;
  standardReport: AiqTriangleReport;
  advancedReport: AiqTriangleReport | null;
  aiqIndexReport: AiqIndexReport | null;
  hasPostAiq: boolean;
}

export interface AiqIndexReport {
  preIndex: number | null;
  postIndex: number | null;
  improvement: number | null;
  estimatedMean: number;
  estimatedSigma: number;
}

export interface AiqTriangleReport {
  preOverallScore: number | null;
  postOverallScore: number | null;
  preDomainScores: AiqTriangleDomainScores | null;
  postDomainScores: AiqTriangleDomainScores | null;
  thresholds: AiqTriangleThresholds;
}

export interface AiqTriangleDomainScores {
  abstraction: number;
  change: number;
  relation: number;
}

export interface AiqTriangleThresholds {
  firstThird: number;
  secondThird: number;
}

export interface AiqReportSummary {
  preScore: number;
  postScore: number;
  improvement: number;
  improvementPercent: number;
  preCorrectCount: number;
  postCorrectCount: number;
  totalQuestions: number;
  preTotalTimeSec: number;
  postTotalTimeSec: number;
  preCompletedAt: string | null;
  postCompletedAt: string | null;
}

export interface AiqQuestionComparison {
  quizId: string;
  domains: string[];
  difficultyLevel: number;
  preCorrect: boolean;
  postCorrect: boolean;
  preElapsedSec: number | null;
  postElapsedSec: number | null;
  improved: boolean;
  preScoreSimple: number;
  postScoreSimple: number;
  preScoreAdvanced: number | null;
  postScoreAdvanced: number | null;
  advancedImproved: boolean;
}

export interface AiqDomainAnalysis {
  domain: string;
  preCorrect: number;
  postCorrect: number;
  totalQuestions: number;
  preAccuracy: number;
  postAccuracy: number;
  preAvgTimeSec: number;
  postAvgTimeSec: number;
  preScoreSimple: number;
  postScoreSimple: number;
  preScoreAdvanced: number | null;
  postScoreAdvanced: number | null;
}

export interface AiqDifficultyAnalysis {
  difficultyLevel: number;
  preCorrect: number;
  postCorrect: number;
  totalQuestions: number;
  preAccuracy: number;
  postAccuracy: number;
  preAvgTimeSec: number;
  postAvgTimeSec: number;
}

export interface AiqTimeAnalysis {
  preAvgTimeSec: number;
  postAvgTimeSec: number;
  timeImprovedPercent: number;
}

export interface AiqClassReportResponse {
  classSummary: AiqClassSummary;
  domainAnalysis: AiqDomainAnalysis[];
  studentSummaries: AiqStudentSummary[];
  improvementDistribution: AiqImprovementBucket[];
}

export interface AiqClassSummary {
  totalStudents: number;
  completedBothCount: number;
  avgPreScore: number;
  avgPostScore: number;
  avgImprovement: number;
  maxImprovement: number;
  minImprovement: number;
}

export interface AiqStudentSummary {
  userId: number;
  userName: string | null;
  email: string | null;
  preScore: number | null;
  postScore: number | null;
  improvement: number | null;
  preCompletedAt: string | null;
  postCompletedAt: string | null;
  status: 'both_completed' | 'pre_only' | 'not_started';
}

export interface AiqImprovementBucket {
  rangeLabel: string;
  rangeMin: number;
  rangeMax: number;
  count: number;
}

export interface QuizResponseRequest {
  quizId: string;  // Big Integer as string
  answers: unknown[];
  vars?: Record<string, unknown>;
  elapsedSec?: number;
}

export interface SessionCompleteRequest {
  sessionId: number;
}

export interface StartSessionResponse {
  sessionId: number;
}

// ============================================================================
// AIQ API
// ============================================================================

export const aiqApi = {
  /**
   * Get AIQ status for a specific product and class
   */
  getStatus: (productId: number, classProductId?: number) => {
    const params = new URLSearchParams({ productId: String(productId) });
    if (classProductId) params.append('classProductId', String(classProductId));
    return apiClient.get<ApiResponse<AiqStatusResponse>>(`/aiq/status?${params}`);
  },

  /**
   * Start Pre-AIQ session
   */
  startPreAiq: (productId: number, classProductId?: number) => {
    const params = new URLSearchParams({ productId: String(productId) });
    if (classProductId) params.append('classProductId', String(classProductId));
    return apiClient.post<ApiResponse<StartSessionResponse>>(`/aiq/pre/start?${params}`);
  },

  /**
   * Submit Pre-AIQ quiz response
   */
  submitPreAiqResponse: (data: QuizResponseRequest) =>
    apiClient.post<ApiResponse<unknown>>('/aiq/pre/submit-response', data),

  /**
   * Complete Pre-AIQ session
   */
  completePreAiq: (sessionId: number) =>
    apiClient.post<ApiResponse<{ message: string }>>('/aiq/pre/complete', { sessionId }),

  /**
   * Start Post-AIQ session
   */
  startPostAiq: (productId: number, classProductId?: number) => {
    const params = new URLSearchParams({ productId: String(productId) });
    if (classProductId) params.append('classProductId', String(classProductId));
    return apiClient.post<ApiResponse<StartSessionResponse>>(`/aiq/post/start?${params}`);
  },

  /**
   * Submit Post-AIQ quiz response
   */
  submitPostAiqResponse: (data: QuizResponseRequest) =>
    apiClient.post<ApiResponse<unknown>>('/aiq/post/submit-response', data),

  /**
   * Complete Post-AIQ session
   */
  completePostAiq: (sessionId: number) =>
    apiClient.post<ApiResponse<{ message: string }>>('/aiq/post/complete', { sessionId }),

  /**
   * Get AIQ report (when both tests completed)
   */
  getReport: (productId: number, classProductId?: number) => {
    const params = new URLSearchParams({ productId: String(productId) });
    if (classProductId) params.append('classProductId', String(classProductId));
    return apiClient.get<ApiResponse<AiqStatusResponse>>(`/aiq/report?${params}`);
  },

  // ============================================================================
  // Dev Tools
  // ============================================================================

  /**
   * [Dev] AIQ 리셋 (pre/post/all)
   */
  resetAiq: (productId: number, type: 'pre' | 'post' | 'all', classProductId?: number) => {
    const params = new URLSearchParams({ productId: String(productId), type });
    if (classProductId) params.append('classProductId', String(classProductId));
    return apiClient.delete<ApiResponse<{ message: string }>>(`/aiq/dev/reset?${params}`);
  },

  /**
   * [Dev] Pre/Post AIQ 퀴즈 비교
   */
  getQuizComparison: (productId: number, classProductId?: number) => {
    const params = new URLSearchParams({ productId: String(productId) });
    if (classProductId) params.append('classProductId', String(classProductId));
    return apiClient.get<ApiResponse<{
      preQuizIds: string[];
      postQuizIds: string[];
      isSameSet: boolean;
      onlyInPre: string[];
      onlyInPost: string[];
    }>>(`/aiq/dev/quiz-comparison?${params}`);
  },

  // ============================================================================
  // Pool Analysis (검수용)
  // ============================================================================

  /**
   * AIQ 출제 풀 분석
   */
  getPoolAnalysis: (productId: number) =>
    apiClient.get<ApiResponse<AiqPoolAnalysisResponse>>(`/aiq/pool-analysis?productId=${productId}`),

  /**
   * AIQ 출제 시뮬레이션
   */
  simulateSelection: (productId: number) =>
    apiClient.get<ApiResponse<AiqSimulateSelectionResponse>>(`/aiq/simulate-selection?productId=${productId}`),

  /**
   * AIQ 분야 설정 분석
   */
  getDomainAnalysis: (productId: number) =>
    apiClient.get<ApiResponse<AiqDomainAnalysisResponse>>(`/aiq/domain-analysis?productId=${productId}`),

  // ============================================================================
  // Reports
  // ============================================================================

  /**
   * Get individual AIQ comparison report (Pre vs Post)
   */
  getIndividualReport: (productId: number, classProductId?: number) => {
    const params = new URLSearchParams({ productId: String(productId) });
    if (classProductId) params.append('classProductId', String(classProductId));
    return apiClient.get<ApiResponse<AiqIndividualReportResponse>>(`/aiq/report/individual?${params}`);
  },

  /**
   * Get individual AIQ report (Admin: allows pre-only)
   * productId or classProductId required (backend resolves productId from classProductId if omitted)
   */
  getAdminIndividualReport: (userId: number, productId?: number, classProductId?: number) => {
    const params = new URLSearchParams({ userId: String(userId) });
    if (productId) params.append('productId', String(productId));
    if (classProductId) params.append('classProductId', String(classProductId));
    return apiClient.get<ApiResponse<AiqIndividualReportResponse>>(`/aiq/report/admin/individual?${params}`);
  },

  /**
   * Get class-level AIQ report
   */
  getClassReport: (classId: number, productId: number) => {
    const params = new URLSearchParams({
      classId: String(classId),
      productId: String(productId),
    });
    return apiClient.get<ApiResponse<AiqClassReportResponse>>(`/aiq/report/class?${params}`);
  },

  // Helper: Transform backend response to frontend AIQStatus format
  transformToFrontendStatus: (
    data: AiqStatusResponse,
    productId: number,
    classProductId: number
  ): AIQStatus => {
    // Default values for missing data
    const defaultComponentStatus: AiqComponentStatus = {
      status: 'not_started',
      attemptCount: 0,
      bestScore: null,
      latestScore: null,
      passed: false,
      completedAt: null,
    };

    const preAiq = data?.preAiq ?? defaultComponentStatus;
    const postAiq = data?.postAiq ?? defaultComponentStatus;

    return {
      productId,
      classProductId,
      preAiqEnabled: data?.preAiqEnabled ?? false,
      postAiqEnabled: data?.postAiqEnabled ?? false,
      preTest: {
        status: preAiq.status,
        score: preAiq.latestScore,
        correctAnswers: null, // Not available in current response
        totalQuestions: 20,
        completedAt: preAiq.completedAt,
      },
      postTest: {
        status: data?.canStartPostAiq
          ? (postAiq.status === 'not_started' ? 'available' : postAiq.status)
          : 'locked',
        score: postAiq.latestScore,
        correctAnswers: null,
        totalQuestions: 20,
        completedAt: postAiq.completedAt,
        unlockCondition: data?.canStartPostAiq ? undefined : '모든 학습을 완료하면 응시할 수 있습니다',
      },
      report: {
        status: (preAiq.status === 'completed' && postAiq.status === 'completed') ? 'available' : 'scheduled',
        reportEnabled: true,
        availableDate: null,
        message: (preAiq.status === 'completed' && postAiq.status === 'completed')
          ? 'Pre-AIQ와 Post-AIQ 결과 비교 리포트입니다'
          : 'Pre-AIQ와 Post-AIQ를 모두 완료하면 리포트를 확인할 수 있습니다',
      },
    };
  },
};
